import type { Dayjs } from 'dayjs';
import {
  type VisitQueryDto,
  type CreateVisitType,
  type VisitForm,
  type ClientVisitSummaryDto,
  getVisitUrlComposed,
} from './entity';
import { apiCall } from '@shared/entity';
import { axiosInstance } from '@libs/axios/axios';
import { CustomerRetentionMetrics } from '@features/finance/pricing-calculator/entity';

export const visitApi = {
  get: (date?: Dayjs, query?: { from?: Dayjs; to?: Dayjs }) => getVisitUrlComposed(date, query),
  updateStatus: () => '/api/visits/status',
  delete: (visitId: string) => `/api/visits/client/${encodeURIComponent(visitId)}`,
  getById: (visitId: string) => `/api/visits/${encodeURIComponent(visitId)}`,
  getCustomerRetention: (month: Dayjs) =>
    `/api/visits/customer-retention?month=${month.format('YYYY-MM-DD')}`,
};

export const postCreateVisit = async (visitData: CreateVisitType): Promise<CreateVisitType> =>
  apiCall(
    async () => await axiosInstance.post(visitApi.get(), visitData),
    'api.visits.createError',
  );

export const getVisitByVisitId = async (visitId: string): Promise<VisitQueryDto> =>
  apiCall(async () => await axiosInstance.get(visitApi.getById(visitId)), 'api.visits.notFound');

export const patchUpdateVisit = async (visitId: string, visitData: VisitForm): Promise<VisitForm> =>
  apiCall(
    async () => await axiosInstance.patch(visitApi.getById(visitId), visitData),
    'api.visits.updateError',
  );

export const deleteVisit = async (visitId: string): Promise<string> =>
  apiCall(
    async () => await axiosInstance.delete(visitApi.delete(visitId)),
    'api.visits.deleteError',
  );

export const patchUpdateVisitStatus = async (data: { visitId?: string; status: boolean }) =>
  apiCall(
    async () => await axiosInstance.patch(visitApi.updateStatus(), data),
    'api.visits.statusError',
  );

export const getVisits = async (
  query?: { from?: Dayjs; to?: Dayjs },
  date?: Dayjs,
): Promise<ClientVisitSummaryDto[]> =>
  apiCall(
    async () => await axiosInstance.get(visitApi.get(date, query)),
    'api.visits.listNotFound',
  );

export const getCustomerRetentionByMonth = async (
  month: Dayjs,
): Promise<CustomerRetentionMetrics> =>
  apiCall(
    async () => await axiosInstance.get(visitApi.getCustomerRetention(month)),
    'api.visits.customerRetentionError',
  );

export const photoApi = {
  upload: () => '/api/photos/upload',
  getByVisitId: (visitId: string) => `/api/photos/${encodeURIComponent(visitId)}`,
  delete: (photoId: string) => `/api/photos/${encodeURIComponent(photoId)}`,
};

export interface Photo {
  id: string;
  url: string;
  description?: string | null;
  uploadedAt: Date;
  visitId: string;
}

export const uploadPhoto = async (
  visitId: string,
  imageData: string,
  description?: string,
): Promise<Photo> =>
  apiCall(
    async () =>
      await axiosInstance.post(photoApi.upload(), {
        visitId,
        imageData,
        description,
      }),
    'api.visits.photoUploadError',
  );

export const getPhotosByVisitId = async (visitId: string): Promise<Photo[]> =>
  apiCall(
    async () => await axiosInstance.get(photoApi.getByVisitId(visitId)),
    'api.visits.photosNotFound',
  );

export const deletePhoto = async (photoId: string, photoUrl: string): Promise<void> =>
  apiCall(
    async () => await axiosInstance.delete(photoApi.delete(photoId), { data: { photoUrl } }),
    'api.visits.photoDeleteError',
  );
